import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic'

export type Prices = {
  retailPrice: number | undefined
  singlePrice: number | undefined
  doublePrice: number | undefined
  memberPrice: number | undefined
} | null
export const pricingStrategies = ['invalid', 'retailOnly', 'singleOnly', 'memberAndRetail', 'doubleAndRetail'] as const
export type PricingStrategy = (typeof pricingStrategies)[number]

export const mapPrices = (prices: ShopperProductsTypes.ProductPriceTable[]): Prices => {
  if (!prices) {
    return null
  }

  const retailKey = prices.find(
    (price) => price.pricebook?.endsWith('-retail-prices') || price.pricebook?.endsWith('RetailPrices'),
  )
  const singleKey = prices.find(
    (price) => price.pricebook?.endsWith('-single-prices') || price.pricebook?.endsWith('SinglePrices'),
  )
  const doubleKey = prices.find(
    (price) => price.pricebook?.endsWith('-double-prices') || price.pricebook?.endsWith('DoublePrices'),
  )
  const memberKey = prices.find(
    (price) => price.pricebook?.endsWith('-member-prices') || price.pricebook?.endsWith('MemberPrices'),
  )

  return {
    retailPrice: retailKey?.price,
    singlePrice: singleKey?.price,
    doublePrice: doubleKey?.price,
    memberPrice: memberKey?.price,
  }
}

export const mapConstructorPrices = (prices: { [key: string]: any }): Prices => {
  if (!prices) {
    return null
  }

  let retailPrices = null
  if (['retail', 'member'].includes(prices.type)) {
    retailPrices = prices.amount
  }
  if (prices.type === 'double') {
    retailPrices = prices.retailPrice
  }

  return {
    retailPrice: retailPrices,
    singlePrice: prices.type === 'single' ? prices.amount : undefined,
    doublePrice: prices.type === 'double' ? prices.amount : undefined,
    memberPrice: prices.type === 'member' ? prices.memberPrice : undefined,
  }
}

export function getMappedPrices(prices: any) {
  const mappedPrices = prices?.isFromConstructor ? mapConstructorPrices(prices) : mapPrices(prices)
  return mappedPrices
}

export const getProductPrice = (
  tieredPrices?: ShopperProductsTypes.ProductPriceTable[],
  isMember?: boolean,
  price?: number,
): number => {
  if (!tieredPrices) {
    return price || 0
  }

  const mappedPrices = tieredPrices && getMappedPrices(tieredPrices)

  if (mappedPrices?.singlePrice) {
    return mappedPrices.singlePrice
  }
  if (mappedPrices?.retailPrice && !mappedPrices?.doublePrice && !mappedPrices?.memberPrice) {
    return mappedPrices.retailPrice
  }
  if (mappedPrices?.doublePrice && mappedPrices?.retailPrice) {
    return mappedPrices.doublePrice
  }
  if (mappedPrices?.memberPrice && mappedPrices?.retailPrice) {
    return isMember ? mappedPrices.memberPrice : mappedPrices.retailPrice
  }

  return 0
}
